import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";

const STORAGE_KEY = "habitaloro.consent.v1";

function updateConsent(consent) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = window.gtag || gtag;
  window.gtag("consent", "update", {
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",
    analytics_storage: consent.analytics ? "granted" : "denied",
    functionality_storage: consent.necessary ? "granted" : "denied",
    personalization_storage: consent.personalization ? "granted" : "denied",
    security_storage: "granted",
  });
}

export default function CookieBanner() {
  const { lang, t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    personalization: false,
  });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        setVisible(true);
      } else {
        const parsed = JSON.parse(stored);
        updateConsent(parsed);
      }
    } catch (_) {
      setVisible(true);
    }
  }, []);

  const persist = (consent) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    } catch (_) {
      /* ignore */
    }
    updateConsent(consent);
    setVisible(false);
    setShowSettings(false);
  };

  const acceptAll = () =>
    persist({
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
    });

  const rejectAll = () =>
    persist({
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false,
    });

  const savePrefs = () => persist(prefs);

  if (!visible) return null;

  const categories = ["necessary", "analytics", "marketing", "personalization"];

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-4 bottom-4 lg:inset-x-auto lg:right-6 lg:bottom-6 z-[60] max-w-2xl bg-white border border-slate-200 shadow-lg rounded-sm"
    >
      {!showSettings ? (
        <div className="p-6 space-y-4">
          <h3
            className="text-lg text-slate-900"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("cookieBanner.title")}
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            {t("cookieBanner.text")}{" "}
            <NavLink
              to={`/${lang}/cookies`}
              className="underline text-slate-800 hover:text-slate-900"
              data-testid="cookie-banner-policy-link"
            >
              {t("footer.links.cookies")}
            </NavLink>
          </p>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              onClick={acceptAll}
              data-testid="cookie-accept-all"
              className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"
            >
              {t("cookieBanner.acceptAll")}
            </Button>
            <Button
              onClick={rejectAll}
              data-testid="cookie-reject-all"
              variant="outline"
              className="rounded-sm border-slate-300 text-slate-800"
            >
              {t("cookieBanner.rejectAll")}
            </Button>
            <Button
              onClick={() => setShowSettings(true)}
              data-testid="cookie-preferences"
              variant="ghost"
              className="rounded-sm text-slate-700"
            >
              {t("cookieBanner.preferences")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-6 space-y-5">
          <h3
            className="text-lg text-slate-900"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("cookieBanner.settingsTitle")}
          </h3>
          <p className="text-sm text-slate-600">
            {t("cookieBanner.settingsText")}
          </p>
          <div className="space-y-3">
            {categories.map((cat) => {
              const disabled = cat === "necessary";
              return (
                <label
                  key={cat}
                  data-testid={`cookie-cat-${cat}`}
                  className="flex items-start gap-3 p-3 border border-slate-200 rounded-sm"
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-slate-900"
                    checked={prefs[cat]}
                    disabled={disabled}
                    onChange={(e) =>
                      setPrefs((p) => ({ ...p, [cat]: e.target.checked }))
                    }
                    data-testid={`cookie-cat-${cat}-input`}
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-900">
                      {t(`cookieBanner.categories.${cat}.title`)}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      {t(`cookieBanner.categories.${cat}.text`)}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={savePrefs}
              data-testid="cookie-save"
              className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"
            >
              {t("cookieBanner.save")}
            </Button>
            <Button
              onClick={acceptAll}
              data-testid="cookie-accept-all-2"
              variant="outline"
              className="rounded-sm border-slate-300 text-slate-800"
            >
              {t("cookieBanner.acceptAll")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
